// DISCLAIMER
// Copyright 2026 ArangoDB GmbH, Cologne, Germany

import React, { useState } from "react";
import { Button, Form, Icon, Label, Modal, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { ChartConfiguration, ChartConfigurationEntry, DefaultChart, DefaultChartList } from "../../api/charts/v1/icharts";
import {
  ConfirmWrite,
  decodeValues,
  decodeValuesObject,
  encodeValues,
  flattenValues,
  parseValuesText,
  redundantHint,
  SourceLabel,
  SourceLegend,
  sourceName,
} from "./utils";
import ValuesModal from "./ValuesModal";

interface DefaultChartsTabArgs {
  defaults?: DefaultChartList;
  config?: ChartConfiguration;
  canEdit: boolean;
  confirm: ConfirmWrite;
  onReload: () => Promise<void>;
  onError: (error: any) => void;
}

// toRequestEntry strips the read-only fields of a configuration entry.
const toRequestEntry = (entry: ChartConfigurationEntry): ChartConfigurationEntry => ({
  name: entry.name,
  version: entry.version,
  has_enabled: entry.has_enabled,
  enabled: entry.enabled,
  values: entry.values,
  addition: entry.addition,
});

interface EntryModalArgs {
  chart?: DefaultChart;
  entry?: ChartConfigurationEntry;
  existingNames: string[];
  onSave: (entry: ChartConfigurationEntry) => void;
  onClose: () => void;
}

// EntryModal edits the configuration entry of one chart, or adds a chart that is not in the template.
const EntryModal = ({ chart, entry, existingNames, onSave, onClose }: EntryModalArgs) => {
  const isNew = !chart && !entry;
  const isAddition = isNew || !!(entry && entry.addition);
  const [name, setName] = useState(chart ? chart.name || "" : "");
  const [version, setVersion] = useState((entry && entry.version) || "");
  const [enabled, setEnabled] = useState<string>(entry && entry.has_enabled ? String(!!entry.enabled) : "");
  const [values, setValues] = useState(decodeValues(entry && entry.values));

  const valuesValid = parseValuesText(values) !== undefined;
  const nameTaken = isNew && existingNames.includes(name.trim());
  const valid = valuesValid && (!isAddition || (!!name.trim() && !!version.trim() && !nameTaken));

  const templateEnabled = chart ? (chart.template_enabled ? "autostart" : "no autostart") : "no autostart";
  const enabledOptions = [
    { key: "", value: "", text: isAddition ? "Not configured (no autostart)" : `Not configured (template: ${templateEnabled})` },
    { key: "true", value: "true", text: "Autostart" },
    { key: "false", value: "false", text: "No autostart" },
  ];

  const handleSave = () =>
    onSave({
      name: name.trim(),
      version: version.trim() || undefined,
      has_enabled: enabled !== "",
      enabled: enabled === "true",
      values: encodeValues(values),
      addition: isAddition,
    });

  return (
    <Modal open onClose={onClose}>
      <Modal.Header>{isNew ? "Add chart" : `Configure ${name}`}</Modal.Header>
      <Modal.Content>
        <Form>
          {isNew && (
            <Form.Input
              required
              label="Name"
              value={name}
              error={nameTaken ? "A chart with this name already exists" : undefined}
              onChange={(_, { value }) => setName(value)}
            />
          )}
          <Form.Input
            required={isAddition}
            label="Version"
            placeholder={chart && chart.template_version ? `Not configured (template: ${chart.template_version})` : ""}
            value={version}
            onChange={(_, { value }) => setVersion(value)}
          />
          <Form.Select label="Autostart" options={enabledOptions} value={enabled} onChange={(_, { value }) => setEnabled(String(value))} />
          <Form.Field error={!valuesValid}>
            <label>Values</label>
            <textarea rows={14} style={{ fontFamily: "monospace" }} placeholder={"replicas: 2"} value={values} onChange={(e) => setValues(e.target.value)} />
            <small>
              {valuesValid
                ? "Sparse Helm values as YAML, merged over the template values. Only enter what differs from the template."
                : "The values must be a YAML mapping."}
            </small>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary icon="check" content="Save" onClick={handleSave} disabled={!valid} />
      </Modal.Actions>
    </Modal>
  );
};

const DefaultChartsTab = ({ defaults, config, canEdit, confirm, onReload, onError }: DefaultChartsTabArgs) => {
  const [editing, setEditing] = useState<{ chart?: DefaultChart; entry?: ChartConfigurationEntry }>();
  const [valuesFor, setValuesFor] = useState<DefaultChart>();

  const items = (defaults && defaults.items) || [];
  const orphaned = (defaults && defaults.orphaned_entries) || [];
  const entries = new Map<string, ChartConfigurationEntry>();
  ((config && config.charts) || []).forEach((entry) => entries.set(entry.name || "", entry));

  const saveEntry = (entry: ChartConfigurationEntry) =>
    confirm({ header: `Save configuration of ${entry.name}?` }, async () => {
      try {
        // Read the configuration again, so that an entry changed elsewhere is not overwritten.
        const current = await apiClients.idashboardClient.GetChartConfiguration();
        const charts = (current.charts || []).filter((c) => c.name !== entry.name).map(toRequestEntry);
        if (entry.version || entry.has_enabled || entry.values || entry.addition) {
          charts.push(entry);
        }
        await apiClients.idashboardClient.UpdateChartConfiguration({
          charts,
          default_values: current.default_values,
          environment_values: current.environment_values,
        });
        setEditing(undefined);
        await onReload();
      } catch (error) {
        onError(error);
      }
    });

  const clearEntry = (name: string, addition: boolean) =>
    confirm(
      {
        header: addition ? `Remove chart ${name}?` : `Clear the configuration of ${name}?`,
        content: addition ? "The chart is removed from the default charts." : "The chart goes back to the values of the template.",
        always: true,
      },
      async () => {
        try {
          await apiClients.idashboardClient.ClearChartConfiguration({ chart_names: [name] });
          await onReload();
        } catch (error) {
          onError(error);
        }
      }
    );

  return (
    <>
      {editing && (
        <EntryModal
          chart={editing.chart}
          entry={editing.entry}
          existingNames={items.map((i) => i.name || "")}
          onSave={saveEntry}
          onClose={() => setEditing(undefined)}
        />
      )}
      {valuesFor && (
        <ValuesModal
          name={valuesFor.name}
          values={valuesFor.values}
          valueSources={valuesFor.value_sources}
          redundantPaths={valuesFor.redundant_value_paths}
          onClose={() => setValuesFor(undefined)}
        />
      )}
      <p>
        These are the charts new deployments get, resolved from the template and the chart configuration. Existing deployments only change when they are synced
        or reset.
      </p>
      <SourceLegend sources={["template", "configuration"]} />
      {canEdit && (
        <Button size="small" icon labelPosition="left" onClick={() => setEditing({})}>
          <Icon name="plus" />
          Add chart
        </Button>
      )}
      <Table striped compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Chart</Table.HeaderCell>
            <Table.HeaderCell>Version</Table.HeaderCell>
            <Table.HeaderCell title="Installed automatically on new deployments. Autostarted charts can deploy other charts from this list.">
              Autostart
            </Table.HeaderCell>
            <Table.HeaderCell>Values</Table.HeaderCell>
            {canEdit && <Table.HeaderCell collapsing />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((chart) => {
            const name = chart.name || "";
            const entry = entries.get(name);
            const paths = Object.keys(flattenValues(decodeValuesObject(chart.values)));
            const configured = Object.keys(chart.value_sources || {}).filter(
              (p) => sourceName(chart.value_sources && chart.value_sources[p]) === "configuration"
            ).length;
            return (
              <Table.Row key={name}>
                <Table.Cell>
                  {name}{" "}
                  {entry && entry.addition && (
                    <Label size="tiny" color="blue">
                      added
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {chart.version} <SourceLabel source={chart.version_source} />
                  {sourceName(chart.version_source) === "configuration" && chart.template_version && chart.template_version !== chart.version && (
                    <small> (template {chart.template_version})</small>
                  )}
                  {chart.version_redundant && (
                    <Label size="tiny" basic color="orange" title={redundantHint}>
                      same as template
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Icon name={chart.enabled ? "check" : "minus"} /> <SourceLabel source={chart.enabled_source} />
                  {chart.enabled_redundant && (
                    <Label size="tiny" basic color="orange" title={redundantHint}>
                      same as template
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {paths.length === 0 ? (
                    "-"
                  ) : (
                    <Button basic compact size="mini" onClick={() => setValuesFor(chart)}>
                      {paths.length} {paths.length === 1 ? "value" : "values"}
                      {configured > 0 ? `, ${configured} configured` : ""}
                    </Button>
                  )}
                  {(chart.redundant_value_paths || []).length > 0 && (
                    <Label size="tiny" basic color="orange" title={redundantHint}>
                      {(chart.redundant_value_paths || []).length} same as template
                    </Label>
                  )}
                </Table.Cell>
                {canEdit && (
                  <Table.Cell collapsing>
                    <Button basic compact size="mini" icon="pencil" title="Configure" onClick={() => setEditing({ chart, entry })} />
                    {entry && (
                      <Button
                        basic
                        compact
                        size="mini"
                        icon="trash"
                        title={entry.addition ? "Remove chart" : "Clear configuration"}
                        onClick={() => clearEntry(name, !!entry.addition)}
                      />
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {orphaned.length > 0 && (
        <>
          <h4>Orphaned configuration</h4>
          <p>These charts are configured, but are not in the template (any more).</p>
          <Table compact size="small">
            <Table.Body>
              {orphaned.map((entry) => (
                <Table.Row key={entry.name}>
                  <Table.Cell>{entry.name}</Table.Cell>
                  <Table.Cell collapsing>
                    {canEdit && <Button basic compact size="mini" icon="trash" title="Remove" onClick={() => clearEntry(entry.name || "", false)} />}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </>
  );
};

export default DefaultChartsTab;
